/**
 * Print a fiscal receipt by opening a printer-friendly popup and triggering print.
 *
 * The cafe's HDM (fiscal device) already prints the legal thermal receipt at
 * checkout; this helper produces a back-office reprint / PDF (via browser print dialog)
 * for record-keeping and B2B invoices.
 */
import type { Database } from '@cafe-pos/supabase';
import {
  LOYALTY_POINT_NAME,
  orderExpectedCharge,
  orderLineItemsSubtotal,
  orderLoyaltyDiscountAmt,
  orderTipAmount,
} from '@cafe-pos/shared';
import i18n from '../i18n.js';

type OrderRow = Database['public']['Tables']['orders']['Row'];
type OrderItemRow = Database['public']['Tables']['order_items']['Row'];
type PaymentRow = Database['public']['Tables']['payments']['Row'];

interface FiscalLike {
  fiscal?: string | number;
  crn?: string;
  receiptNo?: string | number;
  qr?: string;
  [k: string]: unknown;
}

const AMD = (n: number) => `${Number(n).toLocaleString('en-US')} ֏`;

function fmtDate(iso: string) {
  return new Date(iso).toLocaleString('en-GB', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

export function printReceipt(args: {
  order: OrderRow;
  items: OrderItemRow[];
  payments: PaymentRow[];
  clientName: string;
}) {
  const { order, items, payments, clientName } = args;
  const fiscal = (order.fiscal as FiscalLike | null) ?? null;
  const itemsSubtotal = orderLineItemsSubtotal(items);
  const loyaltyDiscount = orderLoyaltyDiscountAmt(order);
  const amountDue = Number(order.total);
  const tip = orderTipAmount(order.tip_amount);
  const charged = orderExpectedCharge(amountDue, order.tip_amount);
  const coinsRedeemed = Math.max(0, Number(order.loyalty_coins_redeemed) || 0);

  const html = `<!doctype html>
<html><head><meta charset="utf-8" />
<title>Receipt #${order.number}</title>
<style>
  @page { size: 80mm auto; margin: 4mm; }
  body { font-family: -apple-system, system-ui, sans-serif; font-size: 12px;
         max-width: 78mm; margin: 0 auto; color: #000; }
  h1 { font-size: 14px; margin: 4px 0; text-align: center; }
  .meta { text-align: center; color: #555; font-size: 11px; margin-bottom: 8px; }
  hr { border: 0; border-top: 1px dashed #999; margin: 6px 0; }
  table { width: 100%; border-collapse: collapse; }
  td { padding: 2px 0; vertical-align: top; }
  .right { text-align: right; }
  .total { font-weight: bold; font-size: 13px; }
  .small { font-size: 10px; color: #555; }
  .center { text-align: center; }
  @media print { .no-print { display: none; } }
</style></head>
<body>
  <h1>${escapeHtml(clientName)}</h1>
  <div class="meta">
    Order #${order.number} · ${fmtDate(order.created_at)}
    ${order.partner_tin ? `<br />TIN: ${escapeHtml(order.partner_tin)}` : ''}
  </div>
  <hr />
  <table>
    ${items
      .map(
        (it) => `
      <tr>
        <td>${escapeHtml(it.name)}</td>
        <td class="right">${it.quantity} × ${AMD(it.unit_price)}</td>
      </tr>
      ${
        it.discount
          ? `<tr><td class="small">discount</td><td class="right small">−${AMD(it.discount)}</td></tr>`
          : ''
      }
      ${
        it.comment?.trim()
          ? `<tr><td class="small" colspan="2"><em>${escapeHtml(it.comment.trim())}</em></td></tr>`
          : ''
      }
      <tr>
        <td class="small">${escapeHtml(it.unit)}</td>
        <td class="right">${AMD(it.unit_price * it.quantity - (it.discount ?? 0))}</td>
      </tr>`,
      )
      .join('')}
  </table>
  <hr />
  <table>
    <tr><td>Subtotal</td><td class="right">${AMD(itemsSubtotal)}</td></tr>
    ${
      loyaltyDiscount > 0
        ? `<tr><td>Loyalty discount</td><td class="right">−${AMD(loyaltyDiscount)}</td></tr>`
        : ''
    }
    ${
      coinsRedeemed > 0
        ? `<tr><td class="small">${LOYALTY_POINT_NAME} redeemed</td><td class="right small">${coinsRedeemed}</td></tr>`
        : ''
    }
    <tr class="total"><td>Amount due</td><td class="right">${AMD(amountDue)}</td></tr>
    ${
      tip > 0
        ? `<tr><td>Card tip</td><td class="right">${AMD(tip)}</td></tr>
           <tr class="total"><td>Charged</td><td class="right">${AMD(charged)}</td></tr>`
        : ''
    }
  </table>
  <hr />
  <table>
    ${payments
      .map(
        (p) => `<tr><td>${escapeHtml(p.method)}</td><td class="right">${AMD(p.amount)}</td></tr>`,
      )
      .join('')}
  </table>
  ${
    fiscal
      ? `<hr /><div class="small center">
          Fiscal #${escapeHtml(String(fiscal.fiscal ?? fiscal.receiptNo ?? ''))}<br />
          ${fiscal.crn ? `CRN: ${escapeHtml(String(fiscal.crn))}` : ''}
        </div>`
      : ''
  }
  <div class="center small" style="margin-top: 12px;">Thank you!</div>
  <div class="no-print center" style="margin-top:16px;">
    <button onclick="window.print()">Print</button>
    <button onclick="window.close()">Close</button>
  </div>
  <script>setTimeout(function() { window.print(); }, 200);</script>
</body></html>`;

  const w = window.open('', '_blank', 'width=420,height=720');
  if (!w) {
    alert(i18n.t('receipt.popupBlocked'));
    return;
  }
  w.document.write(html);
  w.document.close();
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;',
  );
}
